// src/components/finance/PaymentDistributionPanel.jsx
import React, { useState, useEffect } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Sector
} from 'recharts';
import {
    Box,
    Paper,
    Typography,
    Switch,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Card,
    CardContent
} from '@mui/material';
import {
    DonutLarge as DonutIcon,
    Payment as PaymentIcon,
    Payments as PaymentsIcon,
    Info as InfoIcon
} from '@mui/icons-material';

// Componente para el sector activo en el gráfico
const renderActiveShape = (props) => {
    const {
        cx, cy, innerRadius, outerRadius, startAngle, endAngle,
        fill, payload, percent, value
    } = props;

    // Formatear el valor como moneda
    const formattedValue = new Intl.NumberFormat('es-PE', {
        style: 'currency',
        currency: 'PEN'
    }).format(value);

    return (
        <g>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 6}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
            <Sector
                cx={cx}
                cy={cy}
                startAngle={startAngle}
                endAngle={endAngle}
                innerRadius={outerRadius + 6}
                outerRadius={outerRadius + 10}
                fill={fill}
            />
            <text
                x={cx}
                y={cy}
                dy={-20}
                textAnchor="middle"
                fill="#333"
                style={{ fontWeight: 'bold', fontSize: '1rem' }}
            >
                {payload.name}
            </text>
            <text
                x={cx}
                y={cy}
                dy={8}
                textAnchor="middle"
                fill="#666"
            >
                {formattedValue}
            </text>
            <text
                x={cx}
                y={cy}
                dy={25}
                textAnchor="middle"
                fill="#999"
            >
                {`(${(percent * 100).toFixed(1)}%)`}
            </text>
        </g>
    );
};

// Tooltip personalizado
const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const formattedValue = new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN'
        }).format(payload[0].value);

        return (
            <Card className="p-2 border shadow-md" style={{ maxWidth: 220 }}>
                <Typography className="font-bold mb-1" style={{ color: payload[0].color }}>
                    {payload[0].name}
                </Typography>
                <Typography variant="body2" className="font-semibold">
                    {formattedValue}
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                    {`${payload[0].payload.count || 0} pagos (${payload[0].payload.percentage}%)`}
                </Typography>
            </Card>
        );
    }
    return null;
};

const PaymentDistributionPanel = ({ paymentMethods = [] }) => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const [processedData, setProcessedData] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalPayments, setTotalPayments] = useState(0);

    // Colores vibrantes para los métodos de pago
    const COLORS = [
        '#4f46e5', // Indigo
        '#06b6d4', // Cyan
        '#8b5cf6', // Purple
        '#10b981', // Emerald
        '#f59e0b', // Amber
        '#ef4444', // Red
        '#3b82f6', // Blue
        '#ec4899', // Pink
        '#14b8a6', // Teal
        '#f97316'  // Orange
    ];

    // Procesamiento de datos
    useEffect(() => {
        if (!Array.isArray(paymentMethods) || paymentMethods.length === 0) {
            setProcessedData([]);
            setTotalAmount(0);
            setTotalPayments(0);
            return;
        }

        // Asegurar que los valores son números
        const processed = paymentMethods.map(item => ({
            ...item,
            value: parseFloat(item.value) || 0,
            count: parseInt(item.count) || 0,
            percentage: parseFloat(item.percentage) || 0
        }));

        // Calcular totales
        const calculatedTotal = processed.reduce((sum, item) => sum + item.value, 0);
        const paymentsCount = processed.reduce((sum, item) => sum + item.count, 0);

        // Recalcular porcentajes si es necesario
        const withPercentages = processed.map(item => ({
            ...item,
            percentage: calculatedTotal > 0
                ? parseFloat(((item.value / calculatedTotal) * 100).toFixed(1))
                : 0
        }));

        setProcessedData(withPercentages);
        setTotalAmount(calculatedTotal);
        setTotalPayments(paymentsCount);
    }, [paymentMethods]);

    // Manejadores para interactividad
    const onPieEnter = (_, index) => {
        setActiveIndex(index);
    };

    const onPieLeave = () => {
        setActiveIndex(null);
    };

    // Formato para moneda
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN'
        }).format(value);
    };

    return (
        <Paper
            className="h-full rounded-xl transition-all duration-300 hover:shadow-lg"
            style={{
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.05)',
                overflow: 'visible'
            }}
        >
            <CardContent className="p-4">
                <Box className="flex justify-between items-center mb-4">
                    <Box className="flex items-center">
                        <DonutIcon className="text-purple-500 mr-2" />
                        <Typography variant="h6" className="font-semibold">
                            Distribución de Pagos
                        </Typography>
                    </Box>
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showTable}
                                    onChange={() => setShowTable(!showTable)}
                                    size="small"
                                />
                            }
                            label={<Typography variant="caption">Ver tabla</Typography>}
                            labelPlacement="start"
                        />
                        <IconButton
                            size="small"
                            className="ml-1 bg-blue-50"
                            title="Distribución de ingresos por método de pago"
                        >
                            <InfoIcon fontSize="small" className="text-blue-500" />
                        </IconButton>
                    </Box>
                </Box>

                {processedData.length === 0 ? (
                    <Box className="flex flex-col items-center justify-center h-64 text-gray-500">
                        <PaymentsIcon style={{ fontSize: 48 }} className="mb-4 text-gray-400" />
                        <Typography variant="body1" className="mb-2">
                            No hay datos de pagos disponibles
                        </Typography>
                        <Typography variant="body2" className="text-center text-gray-400">
                            Los datos de distribución de pagos aparecerán aquí cuando haya transacciones registradas.
                        </Typography>
                    </Box>
                ) : (
                    <Box className="flex flex-col h-full">
                        {!showTable ? (
                            <>
                                <Box className="h-72">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                                            <Pie
                                                activeIndex={activeIndex}
                                                activeShape={renderActiveShape}
                                                data={processedData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={3}
                                                dataKey="value"
                                                onMouseEnter={onPieEnter}
                                                onMouseLeave={onPieLeave}
                                            >
                                                {processedData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={COLORS[index % COLORS.length]}
                                                        stroke="#ffffff"
                                                        strokeWidth={2}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend
                                                layout="horizontal"
                                                verticalAlign="bottom"
                                                align="center"
                                                formatter={(value, entry, index) => (
                                                    <span style={{ color: COLORS[index % COLORS.length], fontWeight: 500 }}>
                                                        {value}
                                                    </span>
                                                )}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>

                                <Box className="flex justify-center gap-4 mt-2 mb-2">
                                    <Box className="flex items-center bg-purple-50 px-3 py-1 rounded-lg">
                                        <PaymentsIcon className="text-purple-500 mr-1" fontSize="small" />
                                        <Typography variant="body2">
                                            <strong>{totalPayments}</strong> pagos registrados
                                        </Typography>
                                    </Box>

                                    <Box className="flex items-center bg-green-50 px-3 py-1 rounded-lg">
                                        <PaymentIcon className="text-green-500 mr-1" fontSize="small" />
                                        <Typography variant="body2">
                                            Total: <strong>{formatCurrency(totalAmount)}</strong>
                                        </Typography>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <TableContainer component={Paper} className="shadow-none mt-2" style={{ height: 280, overflow: 'auto' }}>
                                <Table size="small" aria-label="tabla de distribución de pagos">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Método de Pago</TableCell>
                                            <TableCell align="right">Cantidad</TableCell>
                                            <TableCell align="right">Monto</TableCell>
                                            <TableCell align="right">Porcentaje</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {processedData.map((row, index) => (
                                            <TableRow
                                                key={row.id || row.name || `payment-${index}`}
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    <Box className="flex items-center">
                                                        <span
                                                            className="inline-block w-3 h-3 rounded-full mr-2"
                                                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                                        />
                                                        {row.name}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">{row.count}</TableCell>
                                                <TableCell align="right">{formatCurrency(row.value)}</TableCell>
                                                <TableCell align="right">{row.percentage}%</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow className="bg-purple-50">
                                            <TableCell component="th" scope="row" className="font-bold">
                                                Total
                                            </TableCell>
                                            <TableCell align="right" className="font-bold">{totalPayments}</TableCell>
                                            <TableCell align="right" className="font-bold">{formatCurrency(totalAmount)}</TableCell>
                                            <TableCell align="right" className="font-bold">{totalAmount > 0 ? '100%' : '0%'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}
            </CardContent>
        </Paper>
    );
};

export default PaymentDistributionPanel;